import type {
  ChatAttachmentDto,
  ChatConversationDto,
  ChatMessageDto,
  ChatMessagePage,
  ChatMyStateBody,
  ChatSubjectType,
  ChatUnreadSummary,
  ChatUser,
  CreateChatConversationBody,
  SendChatMessageBody,
} from '@nexops/shared';
import { CHAT_PAGE_SIZE } from '@nexops/shared';
import { API_BASE_URL, api } from '@/shared/lib/api';

export const chatKeys = {
  all: ['chat'] as const,
  conversations: (archived: boolean) => ['chat', 'conversations', archived ? 'archived' : 'active'] as const,
  conversation: (id: string) => ['chat', 'conversation', id] as const,
  messages: (id: string) => ['chat', 'messages', id] as const,
  unread: ['chat', 'unread'] as const,
  contacts: (search: string) => ['chat', 'contacts', search] as const,
};

const base = '/chat';

export const chatApi = {
  conversations: (archived: boolean) => api.get<ChatConversationDto[]>(`${base}/conversations`, { archived }),
  conversation: (id: string) => api.get<ChatConversationDto>(`${base}/conversations/${id}`),
  bySubject: (type: ChatSubjectType, id: string) => api.get<ChatConversationDto>(`${base}/conversations/by-subject`, { type, id }),
  createDirect: (userId: string) => api.post<ChatConversationDto>(`${base}/conversations/direct`, { userId }),
  create: (body: CreateChatConversationBody) => api.post<ChatConversationDto>(`${base}/conversations`, body),
  setMyState: (id: string, body: ChatMyStateBody) => api.patch<ChatConversationDto>(`${base}/conversations/${id}/me`, body),
  rename: (id: string, title: string) => api.patch<ChatConversationDto>(`${base}/conversations/${id}`, { title }),
  addParticipants: (id: string, userIds: string[]) =>
    api.post<ChatConversationDto>(`${base}/conversations/${id}/participants`, { userIds }),
  removeParticipant: (id: string, userId: string) =>
    api.delete<ChatConversationDto>(`${base}/conversations/${id}/participants/${userId}`),
  markRead: (id: string) => api.post<ChatUnreadSummary>(`${base}/conversations/${id}/read`),
  typing: (id: string) => api.post<void>(`${base}/conversations/${id}/typing`),
  messages: (id: string, before?: string) =>
    api.get<ChatMessagePage>(`${base}/conversations/${id}/messages`, { before, limit: CHAT_PAGE_SIZE }),
  send: (id: string, body: SendChatMessageBody) => api.post<ChatMessageDto>(`${base}/conversations/${id}/messages`, body),
  uploadAttachment: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ChatAttachmentDto>(`${base}/conversations/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    });
  },
  editMessage: (id: string, body: string) => api.patch<ChatMessageDto>(`${base}/messages/${id}`, { body }),
  deleteMessage: (id: string) => api.delete<ChatMessageDto>(`${base}/messages/${id}`),
  react: (id: string, emoji: string) => api.post<ChatMessageDto>(`${base}/messages/${id}/reactions`, { emoji }),
  unread: () => api.get<ChatUnreadSummary>(`${base}/unread`),
  contacts: (search: string) => api.get<ChatUser[]>(`${base}/contacts`, { search: search || undefined }),
};

/** Adres załącznika (przeglądarka dołącza cookie sesji). */
export const attachmentUrl = (id: string): string => `${API_BASE_URL}/chat/attachments/${id}`;
export const streamUrl = (): string => `${API_BASE_URL}/chat/stream`;
